// Mobile app wizard - shared truth for the client SPA and the Worker.
//
// The step list, field limits and completeness rules mirror the Owner
// Dashboard wizard (buddy repo, features/mobile-app + api-internal
// mobile-app-onboarding constants) 1:1 in SEMANTICS. The visual layer is
// Launchpad's own. Rado's shared-DB schema maps onto these payloads; the
// contract is documented in docs/mobile-app-wizard.md.

export const APP_WIZARD_STEPS = [
  "overview",
  "basics",
  "branding",
  "apple_developer",
  "apple_copy",
  "google_copy",
  "screenshots",
  "subscriptions",
  "legal",
  "review"
] as const;

export type AppWizardStep = (typeof APP_WIZARD_STEPS)[number];

export type AppWizardStepStatus = "todo" | "in_progress" | "done";

/** Store text limits - the stores' real limits, same as the reference. */
export const APP_TEXT_LIMITS = {
  appName: 30,
  subtitle: 30,
  promotionalText: 170,
  keywords: 100,
  description: 4000,
  shortDescription: 80,
  subscriptionDisplayName: 30,
  subscriptionDescription: 45,
  teamId: 20,
  legalEntityName: 200,
  legalEntityAddress: 500,
  businessPhone: 30
} as const;

/** Languages offered in the pickers (ISO 639-1) - the real client base. */
export const APP_LANGUAGES = ["cs", "sk", "en", "de", "pl", "hu", "es", "fr", "it"] as const;

export const APP_LANGUAGE_FLAGS: Record<string, string> = {
  cs: "\u{1F1E8}\u{1F1FF}", sk: "\u{1F1F8}\u{1F1F0}", en: "\u{1F1EC}\u{1F1E7}",
  de: "\u{1F1E9}\u{1F1EA}", pl: "\u{1F1F5}\u{1F1F1}", hu: "\u{1F1ED}\u{1F1FA}",
  es: "\u{1F1EA}\u{1F1F8}", fr: "\u{1F1EB}\u{1F1F7}", it: "\u{1F1EE}\u{1F1F9}"
};

/** Pricing currencies - mirror of the reference STORE_CURRENCIES. */
export const APP_CURRENCIES = [
  "AED", "AUD", "BGN", "BRL", "CAD", "CHF", "CLP", "CNY", "COP", "CZK", "DKK", "EGP",
  "EUR", "GBP", "HKD", "HUF", "IDR", "ILS", "INR", "JPY", "KRW", "KZT", "MXN", "MYR",
  "NGN", "NOK", "NZD", "PEN", "PHP", "PKR", "PLN", "QAR", "RON", "SAR", "SEK", "SGD",
  "THB", "TRY", "TWD", "TZS", "USD", "VND", "ZAR"
] as const;

/** Store categories - mirror of the reference APP_CATEGORIES. */
export const APP_CATEGORIES = [
  "books", "business", "education", "entertainment", "finance", "food_drink",
  "graphics_design", "health_fitness", "lifestyle", "medical", "music", "navigation",
  "news", "photo_video", "productivity", "reference", "shopping", "social_networking",
  "sports", "travel", "utilities", "weather"
] as const;

/** Country availability picker, grouped by region (reference COUNTRY_REGIONS). */
export const APP_COUNTRY_REGIONS = [
  { key: "europe", countries: ["CZ", "SK", "DE", "AT", "PL", "HU", "FR", "IT", "ES", "PT", "NL", "BE", "LU", "DK", "SE", "NO", "FI", "IE", "CH", "GR", "RO", "BG", "HR", "SI", "EE", "LV", "LT", "MT", "CY", "GB", "UA", "RS", "AL", "MK", "BA", "ME", "IS"] },
  { key: "americas", countries: ["US", "CA", "BR", "MX", "AR", "CL", "CO", "PE", "EC", "UY", "PY", "BO", "CR", "PA", "DO", "GT"] },
  { key: "asiaPacific", countries: ["JP", "KR", "SG", "HK", "TW", "IN", "AU", "NZ", "ID", "MY", "TH", "VN", "PH", "KZ", "LK", "KH", "MN"] },
  { key: "middleEastAfrica", countries: ["AE", "SA", "QA", "KW", "BH", "OM", "IL", "TR", "EG", "JO", "NG", "ZA", "KE", "GH", "MA", "TN", "DZ"] }
] as const;

export const APP_BILLING_PERIODS = ["weekly", "monthly", "two_months", "three_months", "six_months", "yearly"] as const;
export type AppBillingPeriod = (typeof APP_BILLING_PERIODS)[number];

export const APP_FREE_TRIAL_DAYS = [3, 7, 14, 30, 60, 90] as const;

export const APP_MAX_SUBSCRIPTION_TIERS = 6;

/** Apple price tiers (reference APPLE_PRICE_TIERS): tier_1..tier_50 = $0.99..$49.99, then the extended chart. */
export interface ApplePriceTier { id: string; usd: number }
export const APPLE_PRICE_TIERS: ApplePriceTier[] = [
  ...Array.from({ length: 50 }, (_, index) => ({ id: `tier_${index + 1}`, usd: index + 0.99 })),
  { id: "tier_55", usd: 54.99 }, { id: "tier_60", usd: 59.99 }, { id: "tier_65", usd: 64.99 },
  { id: "tier_70", usd: 69.99 }, { id: "tier_75", usd: 74.99 }, { id: "tier_80", usd: 79.99 },
  { id: "tier_85", usd: 84.99 }, { id: "tier_87", usd: 89.99 }, { id: "tier_90", usd: 99.99 },
  { id: "tier_91", usd: 109.99 }, { id: "tier_92", usd: 119.99 }, { id: "tier_93", usd: 124.99 },
  { id: "tier_94", usd: 129.99 }, { id: "tier_95", usd: 149.99 }, { id: "tier_96", usd: 199.99 },
  { id: "tier_97", usd: 249.99 }, { id: "tier_98", usd: 299.99 }, { id: "tier_99", usd: 399.99 },
  { id: "tier_100", usd: 499.99 }, { id: "tier_101", usd: 999.99 }
];

// ── Asset slots ────────────────────────────────────────────────────────────────

export interface AppAssetSlotSpec {
  slot: AppAssetSlot;
  width: number;
  height: number;
  maxBytes: number;
  contentTypes: readonly string[];
  store: "apple" | "google" | "both";
}

const IPHONE = { width: 1290, height: 2796, maxBytes: 8 * 1024 * 1024, contentTypes: ["image/png", "image/jpeg"], store: "apple" as const };
const IPAD = { width: 2048, height: 2732, maxBytes: 8 * 1024 * 1024, contentTypes: ["image/png", "image/jpeg"], store: "apple" as const };
const GOOGLE = { width: 1080, height: 1920, maxBytes: 8 * 1024 * 1024, contentTypes: ["image/png", "image/jpeg"], store: "google" as const };

export const APP_ASSET_SLOTS = [
  { slot: "icon", width: 1024, height: 1024, maxBytes: 2 * 1024 * 1024, contentTypes: ["image/png"], store: "both" },
  { slot: "feature-graphic", width: 1024, height: 500, maxBytes: 5 * 1024 * 1024, contentTypes: ["image/png", "image/jpeg"], store: "google" },
  { slot: "apple-iphone-1", ...IPHONE }, { slot: "apple-iphone-2", ...IPHONE },
  { slot: "apple-iphone-3", ...IPHONE }, { slot: "apple-iphone-4", ...IPHONE },
  { slot: "apple-ipad-1", ...IPAD }, { slot: "apple-ipad-2", ...IPAD },
  { slot: "apple-ipad-3", ...IPAD }, { slot: "apple-ipad-4", ...IPAD },
  { slot: "google-phone-1", ...GOOGLE }, { slot: "google-phone-2", ...GOOGLE },
  { slot: "google-phone-3", ...GOOGLE }, { slot: "google-phone-4", ...GOOGLE }
] as const satisfies ReadonlyArray<Omit<AppAssetSlotSpec, "slot"> & { slot: string }>;

export type AppAssetSlot =
  | "icon" | "feature-graphic"
  | "apple-iphone-1" | "apple-iphone-2" | "apple-iphone-3" | "apple-iphone-4"
  | "apple-ipad-1" | "apple-ipad-2" | "apple-ipad-3" | "apple-ipad-4"
  | "google-phone-1" | "google-phone-2" | "google-phone-3" | "google-phone-4";

export function appAssetSlotSpec(slot: string): AppAssetSlotSpec | null {
  return (APP_ASSET_SLOTS as ReadonlyArray<AppAssetSlotSpec>).find((item) => item.slot === slot) ?? null;
}

// ── Payload shapes (JSON stored per step) ─────────────────────────────────────

export type LocaleTextMap = Record<string, string>;

export interface AppBasicsPayload {
  stores: { apple: boolean; google: boolean };
  appName: string;
  languages: { primary: string; supported: string[] };
  defaultCurrency: string;
  primaryCategory: string;
  targetCountries: string[];
}

export interface AppBrandingPayload {
  colors: { primary: string; secondary: string; accent: string; background: string };
  /** Launchpad attachment id of the uploaded 1024x1024 PNG icon. */
  iconAttachmentId: string | null;
}

export interface AppAppleDeveloperPayload {
  ownerConfirmedEnrollment: boolean;
  teamId: string;
  appStoreConnectEmail: string;
}

export interface AppAppleCopyPayload {
  subtitle: LocaleTextMap;
  promotionalText: LocaleTextMap;
  keywords: LocaleTextMap;
  description: LocaleTextMap;
}

export interface AppGoogleCopyPayload {
  shortDescription: LocaleTextMap;
  /** Editable only for Google-only apps; otherwise apple_copy owns the listing description. */
  description: LocaleTextMap;
}

export interface AppScreenshotsPayload {
  assets: Partial<Record<Exclude<AppAssetSlot, "icon">, string | null>>;
}

export interface AppSubscriptionTier {
  id: string;
  appleTierId: string;
  billingPeriod: AppBillingPeriod;
  freeTrialDays?: number;
  displayName: LocaleTextMap;
  description: LocaleTextMap;
}

export interface AppSubscriptionsPayload {
  subscriptions: AppSubscriptionTier[];
}

export interface AppLegalPayload {
  legalEntityName: string;
  legalEntityAddress: string;
  businessPhone: string;
  supportEmail: string;
  supportUrl: string;
  marketingUrl: string;
  privacyPolicyUrl: string;
  termsOfServiceUrl: string;
}

export interface AppWizardPayloads {
  basics?: AppBasicsPayload;
  branding?: AppBrandingPayload;
  apple_developer?: AppAppleDeveloperPayload;
  apple_copy?: AppAppleCopyPayload;
  google_copy?: AppGoogleCopyPayload;
  screenshots?: AppScreenshotsPayload;
  subscriptions?: AppSubscriptionsPayload;
  legal?: AppLegalPayload;
}

export interface AppWizardRecord {
  steps: AppWizardPayloads;
  /** Steps that have a saved row (drive the in_progress status). */
  savedSteps: AppWizardStep[];
  submitted: boolean;
  submittedAt: string | null;
}

export interface AppWizardStepState {
  step: AppWizardStep;
  status: AppWizardStepStatus;
  missing: string[];
}

// ── Derived state (pure - identical on client and server) ─────────────────────

export const DEFAULT_STORES = { apple: true, google: true };

function storesOf(record: AppWizardRecord): { apple: boolean; google: boolean } {
  const stores = record.steps.basics?.stores;
  if (!stores || (!stores.apple && !stores.google)) return DEFAULT_STORES;
  return stores;
}

/** Steps that apply to the current store selection, in order (reference wizardStepsForStores). */
export function appWizardStepsFor(record: AppWizardRecord): AppWizardStep[] {
  const stores = storesOf(record);
  return APP_WIZARD_STEPS.filter((step) => {
    if (!stores.apple && (step === "apple_developer" || step === "apple_copy")) return false;
    if (!stores.google && step === "google_copy") return false;
    return true;
  });
}

export function appWizardLocales(record: AppWizardRecord): string[] {
  const languages = record.steps.basics?.languages;
  const all = [languages?.primary ?? "", ...(languages?.supported ?? [])].filter((locale) => locale.length > 0);
  return all.length > 0 ? [...new Set(all)] : [];
}

const hasText = (value: unknown): boolean => typeof value === "string" && value.trim().length > 0;

function requireLocalized(map: LocaleTextMap | undefined, field: string, locales: string[], missing: string[]): void {
  for (const locale of locales) {
    if (!hasText(map?.[locale])) missing.push(`${field}.${locale}`);
  }
}

/**
 * Per-step completeness derived from the data itself - nothing stored,
 * nothing to drift. Mirrors the reference computeStepStatuses: a step with no
 * missing fields is done, a saved-but-incomplete step is in_progress,
 * overview is done once the wizard exists, review is done when all others are.
 */
export function computeAppWizardSteps(record: AppWizardRecord): AppWizardStepState[] {
  const stores = storesOf(record);
  // Copy completeness needs at least one locale; before basics picks the
  // primary language the copy steps count a placeholder requirement.
  const locales = appWizardLocales(record);
  const copyLocales = locales.length > 0 ? locales : ["primary"];
  const states: AppWizardStepState[] = [];

  for (const step of appWizardStepsFor(record)) {
    const missing: string[] = [];
    switch (step) {
      case "overview":
      case "review":
        break;
      case "basics": {
        const basics = record.steps.basics;
        if (!basics?.stores.apple && !basics?.stores.google) missing.push("stores");
        if (!hasText(basics?.appName)) missing.push("appName");
        if (!hasText(basics?.languages.primary)) missing.push("languages.primary");
        if (!hasText(basics?.defaultCurrency)) missing.push("defaultCurrency");
        if (!hasText(basics?.primaryCategory)) missing.push("primaryCategory");
        if ((basics?.targetCountries.length ?? 0) === 0) missing.push("targetCountries");
        break;
      }
      case "branding": {
        const branding = record.steps.branding;
        for (const key of ["primary", "secondary", "accent", "background"] as const) {
          if (!hasText(branding?.colors[key])) missing.push(`colors.${key}`);
        }
        if (!hasText(branding?.iconAttachmentId)) missing.push("icon");
        break;
      }
      case "apple_developer": {
        if (record.steps.apple_developer?.ownerConfirmedEnrollment !== true) missing.push("ownerConfirmedEnrollment");
        break;
      }
      case "apple_copy": {
        const copy = record.steps.apple_copy;
        requireLocalized(copy?.subtitle, "subtitle", copyLocales, missing);
        requireLocalized(copy?.description, "description", copyLocales, missing);
        requireLocalized(copy?.keywords, "keywords", copyLocales, missing);
        break;
      }
      case "google_copy": {
        const copy = record.steps.google_copy;
        requireLocalized(copy?.shortDescription, "shortDescription", copyLocales, missing);
        // Without the App Store the apple_copy step does not exist, so the
        // Play listing description is collected here instead (reference rule).
        if (!stores.apple) requireLocalized(copy?.description, "description", copyLocales, missing);
        break;
      }
      case "screenshots": {
        const assets = record.steps.screenshots?.assets ?? {};
        if (stores.google && !hasText(assets["feature-graphic"])) missing.push("feature-graphic");
        const prefixes = [
          ...(stores.apple ? ["apple-iphone", "apple-ipad"] : []),
          ...(stores.google ? ["google-phone"] : [])
        ];
        for (const prefix of prefixes) {
          for (let index = 1; index <= 4; index += 1) {
            const slot = `${prefix}-${index}` as Exclude<AppAssetSlot, "icon">;
            if (!hasText(assets[slot])) missing.push(slot);
          }
        }
        break;
      }
      case "subscriptions": {
        const tiers = record.steps.subscriptions?.subscriptions ?? [];
        if (tiers.length === 0) missing.push("subscriptions");
        for (const tier of tiers) {
          requireLocalized(tier.displayName, `subscriptions.${tier.id}.displayName`, copyLocales, missing);
        }
        break;
      }
      case "legal": {
        const legal = record.steps.legal;
        for (const key of ["legalEntityName", "legalEntityAddress", "businessPhone", "supportEmail"] as const) {
          if (!hasText(legal?.[key])) missing.push(key);
        }
        if (!hasText(legal?.privacyPolicyUrl)) missing.push("privacyPolicyUrl");
        if (!hasText(legal?.termsOfServiceUrl)) missing.push("termsOfServiceUrl");
        break;
      }
    }
    states.push({ step, status: "todo", missing });
  }

  for (const state of states) {
    if (state.step === "review") continue;
    if (state.step === "overview") state.status = "done";
    else if (state.missing.length === 0) state.status = "done";
    else if (record.savedSteps.includes(state.step)) state.status = "in_progress";
  }

  const review = states.find((state) => state.step === "review");
  if (review) {
    const others = states.filter((state) => state.step !== "review");
    const allDone = others.every((state) => state.status === "done");
    review.status = allDone ? "done" : "todo";
    if (!allDone) review.missing = others.filter((state) => state.status !== "done").map((state) => state.step);
  }
  return states;
}

export interface AppWizardProgress {
  done: number;
  total: number;
  percent: number;
}

export function appWizardProgress(states: AppWizardStepState[]): AppWizardProgress {
  const done = states.filter((state) => state.status === "done").length;
  const total = states.length;
  return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) };
}

// ── API DTOs ──────────────────────────────────────────────────────────────────

export interface AppWizardResponse {
  ok: true;
  wizard: AppWizardRecord;
  states: AppWizardStepState[];
  progress: AppWizardProgress;
  readOnly: boolean;
}

/** Compact summary for /api/home and the staff console client detail. */
export interface AppWizardSummary {
  submitted: boolean;
  submittedAt: string | null;
  progress: AppWizardProgress;
}
